import React, { useState } from 'react';
import { COUNTRY_LIST } from '../../../../../../components/PrintProviders/constants/common';
import { useMutation, useApolloClient, useLazyQuery } from '@apollo/client';
import { cloneDeep } from 'lodash';

import styled from 'styled-components';
import { ExpandableCard, Flexbox, ToolButton } from '@ibinder/common-frontend';
import { ModalFooter, StandardModal } from '@ibinder/ui-common-dialogs';
import { radioChecked, radioUnchecked } from '@ibinder/common-icons';
import {
  DISABLE_ORG_PACKAGE,
  ENABLE_ORG_PACKAGE,
  GET_ORG_PACKAGES,
  GET_PACKAGE_FEATURES
} from 'Shared/api/queries/getOrganizationPackages.query';
import { OrgPropertyDisplay } from './components/OrgPropertyDisplay';
import { useIBTranslation, useModalAction } from '@ibinder/ui-common-hooks';
import { Package } from '../../../../../../utils/constants';

const FakeRadioInput = styled(ToolButton)`
  height: auto;
  > div {
    height: auto;
    display: flex;
    margin: 0;
    align-items: flex-start;
    > span:last-child {
      margin-right: 0;
    }
    background: transparent;
  }
`;

const HiddenRadioInput = styled.input`
  visibility: hidden;
  width: 0px;
  margin: 0px;
`;

const PackageName = styled.span`
  font-weight: bold;
`;

export const PackageHeader = styled.span`
  font-size: 1rem;
  padding-bottom: 1rem;
  color: ${(p) => p.theme.colors.discrete};
  font-weight: bold;
  border-bottom: 1px solid ${(p) => p.theme.colors.discrete20};
`;

const FeatureSpan = styled.span`
  padding-top: 1rem;
  color: ${(p) => p.theme.colors.discrete70};
`;

const ModalContent = styled.div`
  padding: 1rem;
`;

export const sortPackages = (a, b) => {
  const order = [Package.Good, Package.Better, Package.Best, Package.Legacy];

  const aIndex = order.indexOf(a.id);
  const bIndex = order.indexOf(b.id);
  // -1 means package is not in the order array and is moved to end of list
  if (aIndex === -1 && bIndex === -1) {
    return a.translatedName.localeCompare(b.translatedName);
  }
  if (aIndex === -1) return 1;
  if (bIndex === -1) return -1;
  if (aIndex !== bIndex) return aIndex - bIndex;
  return a.translatedName.localeCompare(b.translatedName);
};

const OrganizationPackages = ({
  organizationPackages,
  AllPackages,
  organizationData,
  refetchOrgFeatures
}) => {
  if (!organizationPackages || !AllPackages || !organizationData) {
    return <span>Loading...</span>;
  }

  const { t } = useIBTranslation();
  const client = useApolloClient();

  const [orgPackFeatures, setOrgPackFeatures] = useState({});
  const [selectedPackageId, setSelectedPackageId] = useState(
    organizationPackages.data.get[0]?.id ?? null
  );

  const [getPackageFeatures] = useLazyQuery(GET_PACKAGE_FEATURES, {
    notifyOnNetworkStatusChange: true
  });

  const [enablePackageForOrg] = useMutation(ENABLE_ORG_PACKAGE, {
    onCompleted: () => refetchOrgFeatures()
  });
  const [disablePackageForOrg] = useMutation(DISABLE_ORG_PACKAGE, {
    onCompleted: () => refetchOrgFeatures()
  });

  const [PackageChangeWarningModal, onOpenPackageChangeWarningModal] =
    useModalAction(null, ({ onClose, data: { onCreate } }) => (
      <StandardModal
        open
        headerText={t('packages.legacyPackageChangeWarningHeader')}
        width={500}
        footer={
          <ModalFooter
            onCreateText={t('packages.changePackage')}
            onCreate={() => {
              onClose();
              onCreate();
            }}
            onCancelText={t('packages.keepLegacy')}
            onCancel={onClose}
          />
        }
      >
        <ModalContent>
          {t('packages.legacyPackageChangeWarningBody')}
        </ModalContent>
      </StandardModal>
    ));

  const loadPackageFeatures = (e, id) => {
    getPackageFeatures({
      variables: { packageId: id },
      onCompleted: (res) => {
        orgPackFeatures[id] = res.get;
        setOrgPackFeatures(cloneDeep(orgPackFeatures));
      }
    });
  };

  const handlePackageChange =
    (data: { id: string; legacyPackage: boolean }) =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const previousPackageId = selectedPackageId;
      const newPackageId = data.id;

      if (
        previousPackageId === Package.Legacy &&
        newPackageId !== Package.Legacy
      ) {
        onOpenPackageChangeWarningModal({
          onCreate: () => selectPackage(data)
        });
      } else {
        selectPackage(data);
      }
    };

  const selectPackage = async ({
    id,
    legacyPackage
  }: {
    id: string;
    legacyPackage: boolean;
  }) => {
    // Prepare optimistic update with selected package
    const optimisticArray = [
      { id, __typename: 'OrganizationPackages', legacyPackage }
    ];

    // Update cache optimistically with selected package
    client.cache.writeQuery({
      query: GET_ORG_PACKAGES,
      variables: { organizationId: organizationData.id },
      data: { get: optimisticArray }
    });

    // If there's a currently selected package and it's different from the new selection, disable it
    if (selectedPackageId && selectedPackageId !== id) {
      await disablePackageForOrg({
        variables: {
          organizationId: organizationData.id,
          packageId: selectedPackageId,
          body: {}
        }
      });
    }

    // Enable the newly selected package
    await enablePackageForOrg({
      variables: {
        organizationId: organizationData.id,
        packageId: id,
        body: {}
      }
    }).then(() => {
      setSelectedPackageId(id);
    });
  };

  const filteredPackages = AllPackages.data.get
    .filter((item) => {
      const splittedID = item.id.split('_');

      if (
        splittedID.length === 3 &&
        COUNTRY_LIST[splittedID[0].toLowerCase()].name ===
          organizationData.market.name
      ) {
        return true;
      } else if (splittedID.length === 1) {
        return true;
      }
    })
    .map((item) => ({
      ...item,
      translatedName: t(`packages.${item.id}`)
    }))
    .sort(sortPackages);

  return (
    <>
      {filteredPackages.map((pack) => {
        // Disabled if:
        // - it is a legacy package AND
        // - there is a selected package AND
        // - this package is not the selected one
        const isDisabled =
          pack.legacyPackage &&
          selectedPackageId !== null &&
          selectedPackageId !== pack.id;
        return (
          <ExpandableCard
            showCheckbox={false}
            key={pack.id}
            headerInfo={
              <Flexbox alignItems="center">
                <HiddenRadioInput
                  value={pack.id}
                  name={pack.id}
                  id={pack.id}
                  type="radio"
                  disabled={isDisabled}
                />
                <FakeRadioInput
                  disabled={isDisabled}
                  active={pack.id === selectedPackageId}
                  type="button"
                  onClick={handlePackageChange(pack)}
                >
                  {pack.id === selectedPackageId
                    ? radioChecked
                    : radioUnchecked}
                </FakeRadioInput>
                <PackageName>{pack.translatedName}</PackageName>
              </Flexbox>
            }
            onExpand={(e) => loadPackageFeatures(e, pack.id)}
          >
            <Flexbox flexDirection="column">
              <PackageHeader>{t('packages.featureHeader')}</PackageHeader>
              {!orgPackFeatures[pack.id] ? (
                <>
                  <OrgPropertyDisplay
                    label={'loading'}
                    value={'loading'}
                    loading={true}
                  />{' '}
                  <OrgPropertyDisplay
                    label={'loading'}
                    value={'loading'}
                    loading={true}
                  />{' '}
                  <OrgPropertyDisplay
                    label={'loading'}
                    value={'loading'}
                    loading={true}
                  />
                </>
              ) : (
                orgPackFeatures[pack.id]
                  .sort((a, b) =>
                    t(`DefinedFeatures.${a.id}`).localeCompare(
                      t(`DefinedFeatures.${b.id}`)
                    )
                  )
                  .map((p) => (
                    <FeatureSpan key={p.id}>
                      {t(`DefinedFeatures.${p.id}`)}
                    </FeatureSpan>
                  ))
              )}
            </Flexbox>
          </ExpandableCard>
        );
      })}
      {PackageChangeWarningModal}
    </>
  );
};
export default OrganizationPackages;
